// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
	EditBase,
	TopToolbar,
	SimpleForm,
	TextInput,
	SelectInput,
	BooleanInput,
	required,
	email,
	Toolbar,
	SaveButton,
	DeleteButton,
	useRecordContext,
	useNotify,
	useRefresh,
} from 'react-admin';
import {
	Card,
	CardContent,
	Typography,
	Grid,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField as MuiTextField,
	Box,
	IconButton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { TokenConfigCard } from './TokenConfigCard';

const roleChoices = [
	{ id: 'dev-front', name: 'Développeur Frontend' },
	{ id: 'dev-back', name: 'Développeur Backend' },
	{ id: 'po', name: 'Product Owner' },
	{ id: 'data_analyst', name: 'Data Analyst' },
	{ id: 'admin', name: 'Administrateur' },
];

const Breadcrumb = () => {
	const record = useRecordContext();
	return (
		<TopToolbar>
			<Typography variant="subtitle1">
				Home / MCP Members / {record ? `${record.first_name} ${record.last_name}` : 'Edit'}
			</Typography>
		</TopToolbar>
	);
};

const McpMemberEditToolbar = (props: any) => (
	<Toolbar {...props}>
		<SaveButton />
		<DeleteButton
			mutationMode="pessimistic"
			confirmTitle="Supprimer le membre"
			confirmContent="Êtes-vous sûr de vouloir supprimer ce membre ? Cette action est irréversible."
		/>
	</Toolbar>
);

const RegenerateApiKeyButton = () => {
	const record = useRecordContext();
	const notify = useNotify();
	const refresh = useRefresh();
	const [open, setOpen] = useState(false);
	const [newApiKey, setNewApiKey] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	const handleOpen = () => setOpen(true);
	const handleClose = () => {
		setOpen(false);
		setNewApiKey(null);
	};

	const handleRegenerate = async () => {
		if (!record?.id) return;

		setLoading(true);
		try {
			const token = localStorage.getItem('token');
			const w: any = window;
			const response = await fetch(
				`${w.API_MCP_INTERNE_URL}/api/members/${record.id}/regenerate-key`,
				{
					method: 'POST',
					headers: {
						Authorization: `Bearer ${token}`,
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({}),
				}
			);

			if (!response.ok) {
				throw new Error('Erreur lors de la régénération');
			}

			const data = await response.json();
			setNewApiKey(data.data.api_key);
			notify('API Key régénérée avec succès', { type: 'success' });
			refresh();
		} catch (error) {
			notify("Erreur lors de la régénération de l'API Key", { type: 'error' });
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const handleCopy = () => {
		if (newApiKey) {
			navigator.clipboard.writeText(newApiKey);
			notify('API Key copiée dans le presse-papiers', { type: 'info' });
		}
	};

	return (
		<>
			<Button
				startIcon={<RefreshIcon />}
				onClick={handleOpen}
				variant="outlined"
				color="warning"
				size="small"
				fullWidth
			>
				Régénérer l'API Key
			</Button>

			<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
				<DialogTitle>
					{newApiKey ? 'Nouvelle API Key générée' : "Régénérer l'API Key"}
				</DialogTitle>
				<DialogContent>
					{!newApiKey ? (
						<Typography>
							⚠️ Attention : Cette action va invalider l'API Key actuelle. L'ancien token
							ne fonctionnera plus.
							<br />
							<br />
							Êtes-vous sûr de vouloir régénérer l'API Key ?
						</Typography>
					) : (
						<Box>
							<Typography color="success.main" gutterBottom>
								✅ L'API Key a été régénérée avec succès !
							</Typography>
							<Typography variant="body2" color="text.secondary" gutterBottom>
								⚠️ Copiez cette clé maintenant, elle ne sera plus affichée.
							</Typography>
							<Box sx={{ mt: 2, position: 'relative' }}>
								<MuiTextField
									fullWidth
									value={newApiKey}
									variant="outlined"
									multiline
									rows={3}
									InputProps={{
										readOnly: true,
										endAdornment: (
											<IconButton onClick={handleCopy} edge="end">
												<ContentCopyIcon />
											</IconButton>
										),
									}}
								/>
							</Box>
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					{!newApiKey ? (
						<>
							<Button onClick={handleClose}>Annuler</Button>
							<Button
								onClick={handleRegenerate}
								color="warning"
								variant="contained"
								disabled={loading}
							>
								{loading ? 'Génération...' : 'Régénérer'}
							</Button>
						</>
					) : (
						<Button onClick={handleClose} variant="contained">
							Fermer
						</Button>
					)}
				</DialogActions>
			</Dialog>
		</>
	);
};

const JiraConfigCard = () => {
	const record = useRecordContext();
	const notify = useNotify();
	const [jiraConfig, setJiraConfig] = useState<any>(null);
	const [loading, setLoading] = useState(true);
	const [editing, setEditing] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [formData, setFormData] = useState({
		email: '',
		api_token: '',
	});

	useEffect(() => {
		if (record?.id) {
			loadJiraConfig();
			// Pré-remplir l'email avec celui du membre
			if (!formData.email && record.email) {
				setFormData((prev) => ({ ...prev, email: record.email }));
			}
		}
	}, [record?.id, record?.email]);

	const loadJiraConfig = async () => {
		if (!record?.id) return;

		setLoading(true);
		try {
			const token = localStorage.getItem('token');
			const w: any = window;
			const response = await fetch(
				`${w.API_MCP_INTERNE_URL}/api/configs?member_id=${record.id}&config_type=jira`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			if (response.ok) {
				const data = await response.json();
				if (data.length > 0) {
					const config = data[0];
					setJiraConfig(config);
					setFormData({
						email: config.config_data.email || record.email || '',
						api_token: config.config_data.api_token || '',
					});
				}
			}
		} catch (error) {
			console.error('Erreur lors du chargement de la config Jira:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleSave = async () => {
		if (!record?.id) return;

		try {
			const token = localStorage.getItem('token');
			const w: any = window;

			const payload = {
				member_id: record.id,
				config_type: 'jira',
				config_name: 'Jira Configuration',
				config_data: {
					host: 'quantiio.atlassian.net',
					email: formData.email,
					api_token: formData.api_token,
					project_key: 'QV2',
				},
				is_active: true,
				is_default: true,
			};

			const url = jiraConfig
				? `${w.API_MCP_INTERNE_URL}/api/configs/${jiraConfig.id}`
				: `${w.API_MCP_INTERNE_URL}/api/configs`;
			const method = jiraConfig ? 'PUT' : 'POST';

			const response = await fetch(url, {
				method,
				headers: {
					Authorization: `Bearer ${token}`,
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(payload),
			});

			if (!response.ok) {
				throw new Error('Erreur lors de la sauvegarde');
			}

			notify('Configuration Jira sauvegardée avec succès', { type: 'success' });
			setEditing(false);
			loadJiraConfig();
		} catch (error) {
			notify('Erreur lors de la sauvegarde de la configuration Jira', {
				type: 'error',
			});
			console.error(error);
		}
	};

	const handleDeleteClick = () => {
		setDeleteDialogOpen(true);
	};

	const handleDeleteConfirm = async () => {
		if (!jiraConfig?.id || !record?.id) return;

		setDeleteDialogOpen(false);

		try {
			const token = localStorage.getItem('token');
			const w: any = window;

			const response = await fetch(
				`${w.API_MCP_INTERNE_URL}/api/configs/${jiraConfig.id}`,
				{
					method: 'DELETE',
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			if (!response.ok) {
				throw new Error('Erreur lors de la suppression');
			}

			notify('Configuration Jira supprimée avec succès', { type: 'success' });
			setJiraConfig(null);
			setFormData({ email: record.email || '', api_token: '' });
			setEditing(false);
		} catch (error) {
			notify('Erreur lors de la suppression de la configuration Jira', {
				type: 'error',
			});
			console.error(error);
		}
	};

	const handleDeleteCancel = () => {
		setDeleteDialogOpen(false);
	};

	if (loading) {
		return (
			<Card>
				<CardContent>
					<Typography variant="h6" gutterBottom>
						Configuration Jira
					</Typography>
					<Typography color="text.secondary">Chargement...</Typography>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardContent>
				<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
					<Typography variant="h6">Configuration Jira</Typography>
					{!editing && (
						<Button
							variant="contained"
							color="primary"
							size="small"
							onClick={() => setEditing(true)}
						>
							{jiraConfig ? 'Modifier' : 'Créer'}
						</Button>
					)}
				</div>

				{!editing && jiraConfig ? (
					<Box>
						<Typography variant="caption" color="text.disabled" gutterBottom display="block">
							Host: quantiio.atlassian.net • Project: QV2
						</Typography>
						<Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
							<strong>Email:</strong> {jiraConfig.config_data.email}
						</Typography>
						<Typography variant="body2" color="text.secondary" gutterBottom>
							<strong>API Token:</strong> ••••••••
						</Typography>
					</Box>
				) : !editing ? (
					<Typography color="text.secondary">
						Aucune configuration Jira pour ce membre.
					</Typography>
				) : (
					<Box>
						<Typography variant="caption" color="text.disabled" gutterBottom display="block" sx={{ mb: 2 }}>
							Host: quantiio.atlassian.net • Project: QV2
						</Typography>
						<MuiTextField
							fullWidth
							margin="normal"
							label="Email"
							placeholder="user@quanti.io"
							value={formData.email}
							onChange={(e) => setFormData({ ...formData, email: e.target.value })}
						/>
						<MuiTextField
							fullWidth
							margin="normal"
							label="API Token"
							type="password"
							value={formData.api_token}
							onChange={(e) =>
								setFormData({ ...formData, api_token: e.target.value })
							}
						/>
						<Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
							<Button
								variant="contained"
								color="primary"
								onClick={handleSave}
								fullWidth
							>
								Sauvegarder
							</Button>
							<Button
								variant="outlined"
								onClick={() => {
									setEditing(false);
									if (jiraConfig) {
										setFormData({
											email: jiraConfig.config_data.email || record.email || '',
											api_token: jiraConfig.config_data.api_token || '',
										});
									}
								}}
								fullWidth
							>
								Annuler
							</Button>
						</Box>
						{jiraConfig && (
							<Button
								variant="text"
								color="error"
								onClick={handleDeleteClick}
								fullWidth
								sx={{ mt: 1 }}
							>
								Supprimer la configuration
							</Button>
						)}
					</Box>
				)}

				{/* Dialog de confirmation de suppression */}
				<Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
					<DialogTitle>Supprimer la configuration Jira</DialogTitle>
					<DialogContent>
						<Typography>
							Êtes-vous sûr de vouloir supprimer cette configuration Jira ? Cette action est
							irréversible.
						</Typography>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleDeleteCancel}>Annuler</Button>
						<Button onClick={handleDeleteConfirm} color="error" variant="contained">
							Supprimer
						</Button>
					</DialogActions>
				</Dialog>
			</CardContent>
		</Card>
	);
};


export const McpMemberEdit = (props: any) => {
	return (
		<EditBase>
			<>
				<Breadcrumb />
				<Grid container spacing={2}>
					{/* Colonne centrale avec les configurations */}
					<Grid item xs={8}>
						<JiraConfigCard />
						<Box sx={{ mt: 2 }}>
							<TokenConfigCard
								configType="github"
								title="GitHub"
								tokenLabel="Personal Access Token"
								tokenPlaceholder="ghp_..."
								helperText={
									<>
										Scopes requis : <strong>repo</strong>, <strong>read:org</strong>,{' '}
										<strong>workflow</strong>. Accès aux orgs <strong>quantiio</strong> et{' '}
										<strong>quanti-data-mkt</strong>. Crée le token sur{' '}
										<a
											href="https://github.com/settings/tokens"
											target="_blank"
											rel="noopener noreferrer"
										>
											github.com/settings/tokens
										</a>
										.
									</>
								}
							/>
						</Box>
					</Grid>

					{/* Colonne droite avec le formulaire membre */}
					<Grid item xs={4}>
						<Card>
							<CardContent>
								<Typography variant="h6" gutterBottom>
									Détails du Membre
								</Typography>
								<SimpleForm toolbar={<McpMemberEditToolbar />}>
									<TextInput source="id" label="ID" disabled fullWidth />
									<TextInput
										source="first_name"
										label="Prénom"
										validate={[required()]}
										fullWidth
									/>
									<TextInput
										source="last_name"
										label="Nom"
										validate={[required()]}
										fullWidth
									/>
									<TextInput
										source="email"
										label="Email"
										validate={[required(), email()]}
										fullWidth
									/>
									<SelectInput
										source="role"
										label="Rôle"
										choices={roleChoices}
										validate={[required()]}
										fullWidth
									/>
									<BooleanInput source="is_active" label="Actif" />
									<TextInput
										source="api_key_preview"
										label="API Key (aperçu)"
										disabled
										fullWidth
									/>
									<RegenerateApiKeyButton />
								</SimpleForm>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</>
		</EditBase>
	);
};
